import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useT } from '@transifex/react';

import { CSVLink } from 'react-csv';

import { Layout } from '../../components/Layout';
import { ReportingContent } from '../../components/PagesContent/ReportingContent';
import { ProgressCircle } from '../../components/ProgressCircle';
import { getCSVFileName } from './utils/csvDataHelpers';

import {
  getCSVDataAsync,
  isLoadingCSVDataSelector,
  toggleIsLoadingCSVData,
  downloadProgressSelector,
  csvDataForDownloadSelector,
  csvDataNameSelector,
  setCSVDataName,
  clearCSVDataForDownload,
} from '../../store/analytics';
import { brandInfoSelector } from '../../store/brand';
import { useRouter } from '../../hooks/useRouter';

export const Reporting = () => {
  const { query, params } = useRouter();

  const csvDataName = useSelector(csvDataNameSelector);
  const csvDataForDownload = useSelector(csvDataForDownloadSelector);

  const isLoadingCSVData = useSelector(isLoadingCSVDataSelector);
  const downloadProgress = useSelector(downloadProgressSelector);

  const brandInfo = useSelector(brandInfoSelector);

  const brandId = brandInfo.brandIdFromAdmin;

  const dispatch = useDispatch();
  const t = useT();

  const node = useRef(null);

  const handleCloseProgressModal = useCallback(() => {
    dispatch(toggleIsLoadingCSVData(false));
  }, [dispatch]);

  useEffect(() => {
    if (csvDataForDownload.length) {
      if (node.current) {
        node.current.click();
      }
    }
    return () => {
      dispatch(clearCSVDataForDownload());
    };
  }, [csvDataForDownload, dispatch]);

  const setCSVDataFileName = useMemo(() => {
    return getCSVFileName(csvDataName);
  }, [csvDataName]);

  const type = useMemo(() => {
    if (params.sid) return 'ad';
    if (params.ssid) return 'ad-set';
    return 'campaign';
  }, [params.ssid, params.sid]);

  const handleDownloadCSV = useCallback(
    (csvDataType, questionId, filters) => () => {
      const tab = query.type === 'overview' ? 'overviewTab' : 'demographicTab';
      dispatch(toggleIsLoadingCSVData(true));
      dispatch(clearCSVDataForDownload());
      dispatch(
        getCSVDataAsync.request({
          csvDataType,
          currentTab: tab,
          questionId,
          brandId,
          campaignId: query.cid,
          adSetId: query.ssid,
          adId: query.sid,
          type,
          filters: filters ?? {},
        })
      );
      dispatch(setCSVDataName(csvDataType));
    },
    [dispatch, brandId, query, type]
  );

  return (
    <Layout>
      {isLoadingCSVData && (
        <ProgressCircle
          title={t('Downloading CSV Data')}
          progress={downloadProgress}
          closeModal={handleCloseProgressModal}
        />
      )}
      <ReportingContent handleDownloadCSV={handleDownloadCSV} type={type} />
      <CSVLink data={csvDataForDownload} filename={setCSVDataFileName}>
        <div ref={node} />
      </CSVLink>
    </Layout>
  );
};

export default Reporting;
